<template>
  <div class="flex flex-col overflow-hidden" :style="styles">
    <div
      v-if="webviewStore.isActive"
      class="w-full min-h-[50px] max-h-[50px] flex items-center justify-between border-gray-200 px-2"
    >
      <Icon
        name="ChevronUp"
        :size="30"
        class="-rotate-90 stroke-gray-800"
        @click="handleBackClick"
      />
      <div
        class="font-secondary font-medium text-[16px] leading-[22px] text-gray-800"
      >
        Account Settings
      </div>
      <div class="w-[30px]"></div>
    </div>

    <div class="flex flex-col h-full overflow-hidden" :style="containerStyles">
      <div class="w-full max-w-[918px] mx-auto px-3 tablet-v:px-4">
        <div
          v-if="!webviewStore.isActive"
          class="font-primary font-medium text-[25px] leading-[20px] text-gray-800 my-9"
        >
          Settings
        </div>
      </div>

      <div class="pl-3 min-[918px]:pl-0">
        <Tabs
          v-model="tab"
          :tabs="tabs"
          class="min-[918px]:max-w-[886px] mx-auto"
        />
      </div>

      <div ref="scrollContainer" class="overflow-y-auto pt-10 pb-[90px]">
        <div class="max-w-[918px] mx-auto px-3 tablet-v:px-4">
          <RouterView />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Tabs } from '@dacr/ui'
import { useBillingQueryQuery } from '@/shared/graphql/legacy/generated'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { useDevice } from '@/shared/composables/useDevice'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { Icon } from '@dacr/ui'

const { refetch: refetchBilling } = useBillingQueryQuery(
  {},
  {
    pollInterval: useWebviewStore().isActive ? 10_000 : undefined,
  }
)

const route = useRoute()
const router = useRouter()
const device = useDevice()
const { availableHeight } = useAvailableHeight()
const webviewStore = useWebviewStore()

const scrollContainer = ref<HTMLElement>()

const styles = computed(() => ({
  ...(webviewStore.isActive && {
    height: `${device.viewportHeight.value}px`,
    paddingTop: `${webviewStore.topSafeAreaHeight}px`,
  }),
}))

const containerStyles = computed(() => {
  return {
    ...(webviewStore.isActive && {
      marginTop: '36px',
    }),
    ...(!webviewStore.isActive && {
      height: `${availableHeight.value}px`,
    }),
  }
})

const tab = ref()
const tabs = [
  {
    label: 'Account',
    value: 'account',
  },
  {
    label: 'Plan & Billing',
    value: 'billing',
  },
  {
    label: 'Security',
    value: 'security',
  },
]

watch(tab, () => {
  scrollContainer.value?.scrollTo(0, 0)
  router.push(`/settings/${tab.value}`)
})

watch(
  route,
  () => {
    if (!route.path) return

    tab.value = route.path.replace('/settings/', '')
  },
  {
    deep: true,
    immediate: true,
  }
)

const handleBackClick = () => {
  webviewStore.postMessage({
    type: NativeActionsEnum.NavigationBack,
  })
}

onMounted(() => {
  refetchBilling()
})
</script>
